import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DealCard } from '@/components/DealCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent } from '@/components/ui/card';
import { Search, SlidersHorizontal } from 'lucide-react';
import HowItWorks from '@/components/HowItWorks';
import PropertyFilters from '@/components/PropertyFilters';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DealsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [deals, setDeals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedLocation, setSelectedLocation] = useState(searchParams.get('location') || '');
  const [sortBy, setSortBy] = useState('created_at_desc');
  const [showFilters, setShowFilters] = useState(false);
  
  // Property-specific filters
  const [propertyFilters, setPropertyFilters] = useState({
    location: searchParams.get('prop_location') || 'JLT',
    bedrooms: searchParams.get('bedrooms') || '1',
    propertyType: searchParams.get('property_type') || 'Apartment'
  });

  const emirates = ['All Emirates', 'Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain'];

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchDeals();
  }, [selectedCategory, selectedLocation, sortBy, propertyFilters]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchDeals = async () => {
    setLoading(true);
    try {
      let url = `${API}/deals?sort=${sortBy}`;
      
      if (selectedCategory) {
        url += `&category=${selectedCategory}`;
        
        // Add property-specific filters if Property category is selected
        const propertyCategory = categories.find(c => c.id === selectedCategory);
        if (propertyCategory && propertyCategory.slug === 'property') {
          url += `&prop_location=${encodeURIComponent(propertyFilters.location)}`;
          url += `&bedrooms=${encodeURIComponent(propertyFilters.bedrooms)}`;
          url += `&property_type=${encodeURIComponent(propertyFilters.propertyType)}`;
        }
      }
      
      if (selectedLocation && selectedLocation !== 'All Emirates') {
        url += `&location=${selectedLocation}`;
      }

      const response = await axios.get(url);
      setDeals(response.data);
    } catch (error) {
      console.error('Error fetching deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      fetchDeals();
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/deals/search?q=${encodeURIComponent(searchQuery)}`);
      setDeals(response.data);
      setSearchParams({ q: searchQuery });
    } catch (error) {
      console.error('Error searching deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId === 'all' ? '' : categoryId);
    if (categoryId !== 'all') {
      setSearchParams({ category: categoryId });
    } else {
      setSearchParams({});
    }
  };
  
  const handlePropertyFilterChange = (newFilters) => {
    setPropertyFilters(newFilters);
    setSearchParams({
      category: selectedCategory,
      prop_location: newFilters.location,
      bedrooms: newFilters.bedrooms,
      property_type: newFilters.propertyType
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-6 text-center" data-testid="deals-page-heading">
            Browse All Deals
          </h1>
          
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Search deals, brands, categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 text-gray-900"
                data-testid="deals-search-input"
              />
            </div>
            <Button type="submit" size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900">
              Search
            </Button>
          </form>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Property Filters - Show when Property category is selected */}
        {selectedCategory && categories.find(c => c.id === selectedCategory)?.slug === 'property' && (
          <PropertyFilters 
            onFilterChange={handlePropertyFilterChange}
            initialFilters={propertyFilters}
          />
        )}
        
        {/* How It Works Section - Only show for property category */}
        {selectedCategory && categories.find(c => c.id === selectedCategory)?.slug === 'property' && (
          <div className="mb-8">
            <HowItWorks />
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <Card className="sticky top-4">
              <CardContent className="p-6 space-y-6">
                <div className="flex items-center justify-between lg:hidden">
                  <h3 className="font-semibold">Filters</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <SlidersHorizontal size={20} />
                  </Button>
                </div>

                <div className={`space-y-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                  {/* Category Filter */}
                  <div>
                    <label className="block text-sm font-semibold mb-3">Category</label>
                    <Select value={selectedCategory || 'all'} onValueChange={handleCategoryChange}>
                      <SelectTrigger data-testid="category-filter">
                        <SelectValue placeholder="All Categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Location Filter */}
                  <div>
                    <label className="block text-sm font-semibold mb-3">Location</label>
                    <Select value={selectedLocation || 'All Emirates'} onValueChange={setSelectedLocation}>
                      <SelectTrigger data-testid="location-filter">
                        <SelectValue placeholder="All Emirates" />
                      </SelectTrigger>
                      <SelectContent>
                        {emirates.map((emirate) => (
                          <SelectItem key={emirate} value={emirate}>
                            {emirate}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Sort By */}
                  <div>
                    <label className="block text-sm font-semibold mb-3">Sort By</label>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger data-testid="sort-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="created_at_desc">Newest First</SelectItem>
                        <SelectItem value="discount_desc">Highest Discount</SelectItem>
                        <SelectItem value="price_asc">Price: Low to High</SelectItem>
                        <SelectItem value="price_desc">Price: High to Low</SelectItem>
                        <SelectItem value="popular">Most Popular</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Clear Filters */}
                  {(selectedCategory || (selectedLocation && selectedLocation !== 'All Emirates')) && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setSelectedCategory('');
                        setSelectedLocation('All Emirates');
                        setSearchParams({});
                      }}
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </aside>

          {/* Deals Grid */}
          <main className="flex-1">
            <div className="mb-6 flex justify-between items-center">
              <p className="text-gray-600" data-testid="deals-count">
                {loading ? 'Loading...' : `${deals.length} deals found`}
              </p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-lg h-96 animate-pulse" />
                ))}
              </div>
            ) : deals.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {deals.map((deal) => (
                  <DealCard key={deal.id} deal={deal} onSaveToggle={fetchDeals} />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-gray-600 text-lg mb-4">No deals found matching your criteria</p>
                  <Button
                    onClick={() => {
                      setSelectedCategory('');
                      setSelectedLocation('All Emirates');
                      setSearchQuery('');
                      setSearchParams({});
                    }}
                  >
                    Clear All Filters
                  </Button>
                </CardContent>
              </Card>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};
